import { useCallback, useEffect, useRef, useState } from 'react';
import {
    searchTraders,
    type TraderSearchRequest,
    type TraderSearchResult,
} from '../../api/traderSearch';

// Rows and count are two requests started together (spec 6.7). A filter or
// sort change cancels both and starts both again; a page change loads rows
// only. An address lookup sends the rows request alone and takes the number of
// rows as its total. Aborting the fetch also makes the indexer cancel its query
// (spec 4.3).
// A changed request shows the loading state at once but waits DEBOUNCE_MS
// before both requests go out, so typing or clicking through filters sends
// one search; the first request after mount goes out at once.

export const DEBOUNCE_MS = 400;

export type TraderSearchFailure = 'timeout' | 'unknown_name' | 'failed';

export interface TraderSearchState {
    traders: TraderSearchResult[];
    loading: boolean;
    elapsedSeconds: number;
    failure: TraderSearchFailure | null;
    pageIndex: number;
    hasPrev: boolean;
    hasNext: boolean;
    total: number | null;
    counting: boolean;
    countFailed: boolean;
    nextPage: () => void;
    prevPage: () => void;
    goToPage: (index: number) => void;
}

function failureOf(error: unknown): TraderSearchFailure {
    // ApiError carries the HTTP status (apiClient.ts).
    const status = (error as { status?: unknown } | null)?.status;
    if (status === 504) return 'timeout';
    if (status === 404) return 'unknown_name';
    return 'failed';
}

export function useTraderSearch(
    request: TraderSearchRequest | null,
    pageSize: number
): TraderSearchState {
    const key = request ? JSON.stringify(request) : null;
    const pageKey = key === null ? null : `${key}|${pageSize}`;
    // The page belongs to one request and page size; any other key is page 0.
    const [page, setPage] = useState<{ key: string | null; index: number }>({
        key: null,
        index: 0,
    });
    // A changed key forgets the stored page now, so returning to an earlier
    // request cannot bring its page back. The derived index keeps the first
    // request after the change at offset 0.
    if (page.key !== pageKey && page.index !== 0)
        setPage({ key: pageKey, index: 0 });
    const pageIndex = page.key === pageKey ? page.index : 0;

    const [traders, setTraders] = useState<TraderSearchResult[]>([]);
    const [hasMore, setHasMore] = useState(false);
    // Loading from the first render, or "Nothing matches" would flash
    // before the first effect runs.
    const [loading, setLoading] = useState(request !== null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [failure, setFailure] = useState<TraderSearchFailure | null>(null);
    const [total, setTotal] = useState<number | null>(null);
    const [counting, setCounting] = useState(request !== null);
    const [countFailed, setCountFailed] = useState(false);

    // The request key both effects last started for; null until the first
    // request. The rows effect runs first, so it still sees the previous key
    // on a request change; the count effect updates it.
    const startedKey = useRef<string | null>(null);
    const delayFor = (next: string) =>
        startedKey.current !== null && startedKey.current !== next
            ? DEBOUNCE_MS
            : 0;

    useEffect(() => {
        if (key === null) {
            setLoading(false);
            return;
        }
        const base = JSON.parse(key) as TraderSearchRequest;
        const controller = new AbortController();
        let timer: ReturnType<typeof setInterval> | undefined;
        setLoading(true);
        setFailure(null);
        setElapsedSeconds(0);

        const start = () => {
            const started = Date.now();
            timer = setInterval(
                () =>
                    setElapsedSeconds(
                        Math.floor((Date.now() - started) / 1000)
                    ),
                1000
            );
            searchTraders(
                { ...base, limit: pageSize, offset: pageIndex * pageSize },
                controller.signal
            )
                .then(response => {
                    if (controller.signal.aborted) return;
                    setTraders(response.traders);
                    setHasMore(response.has_more);
                })
                .catch(error => {
                    if (controller.signal.aborted) return;
                    setFailure(failureOf(error));
                    setTraders([]);
                    setHasMore(false);
                })
                .finally(() => {
                    if (controller.signal.aborted) return;
                    clearInterval(timer);
                    setLoading(false);
                });
        };

        const delay = delayFor(key);
        const wait = delay > 0 ? setTimeout(start, delay) : undefined;
        if (delay === 0) start();

        return () => {
            clearTimeout(wait);
            controller.abort();
            clearInterval(timer);
        };
    }, [key, pageSize, pageIndex]);

    useEffect(() => {
        setTotal(null);
        setCountFailed(false);
        if (key === null) {
            setCounting(false);
            return;
        }
        const base = JSON.parse(key) as TraderSearchRequest;
        const delay = delayFor(key);
        startedKey.current = key;
        // An address lookup returns one row at most: its row count is the
        // total, so no count request goes out (see `count` below).
        if (base.address) {
            setCounting(false);
            return;
        }
        const controller = new AbortController();
        setCounting(true);

        const start = () => {
            searchTraders({ ...base, count_only: true }, controller.signal)
                .then(response => {
                    if (controller.signal.aborted) return;
                    if (typeof response.total === 'number')
                        setTotal(response.total);
                    else setCountFailed(true);
                })
                .catch(() => {
                    if (controller.signal.aborted) return;
                    setCountFailed(true);
                })
                .finally(() => {
                    if (controller.signal.aborted) return;
                    setCounting(false);
                });
        };

        const wait = delay > 0 ? setTimeout(start, delay) : undefined;
        if (delay === 0) start();

        return () => {
            clearTimeout(wait);
            controller.abort();
        };
    }, [key]);

    const goToPage = useCallback(
        (index: number) => setPage({ key: pageKey, index: Math.max(0, index) }),
        [pageKey]
    );
    const nextPage = useCallback(
        () => goToPage(pageIndex + 1),
        [goToPage, pageIndex]
    );
    const prevPage = useCallback(
        () => goToPage(pageIndex - 1),
        [goToPage, pageIndex]
    );

    // An address lookup sends no count request: its rows are the whole
    // answer, so the total is their number once they are in, and unavailable
    // when the lookup failed.
    const count = request?.address
        ? loading
            ? { total: null, counting: true, countFailed: false }
            : failure
              ? { total: null, counting: false, countFailed: true }
              : { total: traders.length, counting: false, countFailed: false }
        : { total, counting, countFailed };

    return {
        traders,
        loading,
        elapsedSeconds,
        failure,
        pageIndex,
        hasPrev: pageIndex > 0,
        hasNext: hasMore,
        ...count,
        nextPage,
        prevPage,
        goToPage,
    };
}
